import { v4 as uuidv4 } from 'uuid';
import type { NodeId, InputId } from 'web';
import type { FigmaCompositorData } from '@/types/node';
import type { ImageFile, EvaluatedValue } from '@/designer/designer';
import type { FlowGraph } from '../FlowGraph';

export class FigmaCompositorView {
  constructor(
    private graph: FlowGraph,
    private nodeId: NodeId,
  ) {}

  getInputs(): Record<InputId, EvaluatedValue | undefined> {
    return Object.fromEntries(
      this.graph.getInputIds(this.nodeId).map((id) => [id, this.graph.evaluateInput(this.nodeId, id)]),
    );
  }

  setOutputImage(outputImage: ImageFile): void {
    const data = this.graph.store.getNodeDataNullable<FigmaCompositorData>(this.nodeId);
    if (data?.outputImage?.url === outputImage.url) return;
    this.graph.wasmChange((wasm) => wasm.setFigmaOutputImage(this.nodeId, outputImage));
  }

  getFigmaNodeId(inputId: InputId): string | null {
    return this.graph.wasm.call((wasm) => wasm.getFigmaNodeId(this.nodeId, inputId)) ?? null;
  }

  setFigmaNodeId(inputId: InputId, figmaNodeId: string | null): void {
    if (this.getFigmaNodeId(inputId) === figmaNodeId) return;
    this.graph.edit(() =>
      this.graph.wasmChange((wasm) => wasm.setFigmaNodeId(this.nodeId, inputId, figmaNodeId ?? undefined)),
    );
  }

  addNodeInputHandle() {
    this.graph.edit(() => {
      const existingInputIds = this.graph.getInputIds(this.nodeId);
      const order = existingInputIds.length;
      const newInputKey = `layer_${order + 1}`;

      const undoEntry = this.graph.wasmChange((wasm) => wasm.addInputHandle(this.nodeId, newInputKey));

      undoEntry.add(
        this.graph.addNodeInputHandle(this.nodeId, newInputKey, {
          description: '',
          label: newInputKey,
          format: 'uri',
          id: uuidv4(),
          order,
          required: false,
        }),
      );

      return undoEntry;
    });
  }
}
